import { ComponentWithAs, IconProps, Text, VStack } from '@chakra-ui/react';
import { Input } from '@gamma/ui/form-fields';
import { ILoginForm } from '@gamma/ui/forms';
import { Alert } from '@gamma/ui/overlay';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MFA, MODAL_STEPS } from '../../../../../shared/constants';
import { i18n } from '../../../../../shared/localization';
import { CognitoUserInterface } from '../../../../../shared/types/auth-types';
import { signIn } from '../../../queries';

import { IHelperText, SetStepType } from '../types';

interface AuthLoginBodyProps {
  email: string;
  helperTexts: IHelperText;
  loginIcon?: ComponentWithAs<'svg', IconProps>;
  showAlert?: boolean;
  errorMessage: string;
  width?: string;
  setStep: SetStepType;
  setIsButtonLoading: (e: boolean) => void;
  setIsSignInButtonDisabled: (e: boolean) => void;
  setVerifyUser: (verifyUser: CognitoUserInterface) => void;
}

export const AuthLoginBody = ({
  email,
  helperTexts,
  loginIcon: LoginIcon,
  showAlert,
  errorMessage,
  width = '86%',
  setIsButtonLoading,
  setStep,
  setVerifyUser,
  setIsSignInButtonDisabled,
}: AuthLoginBodyProps) => {
  const { register, handleSubmit, watch, setFocus } = useForm<ILoginForm>();
  const [loginError, setLoginError] = useState<string>('');

  const passwordInput = watch('password');

  useEffect(() => {
    setIsSignInButtonDisabled(!passwordInput);
    setLoginError('');
  }, [passwordInput]);

  useEffect(() => {
    setFocus('password');
  }, []);

  const handleSignIn = async (values: ILoginForm) => {
    setIsButtonLoading(true);
    try {
      const user = await signIn(values);
      if (user) {
        setVerifyUser(user);
        if (
          user.preferredMFA === MFA.NOMFA &&
          user?.attributes?.['custom:allow_no_mfa']
        ) {
          setStep(MODAL_STEPS.details);
        } else {
          setStep(MODAL_STEPS.mfa);
        }
      }
    } catch (error: unknown) {
      console.error(error);
      setLoginError(errorMessage);
    }
    setIsButtonLoading(false);
  };
  return (
    <VStack spacing={4}>
      {LoginIcon && <LoginIcon />}
      <Text alignSelf="flex-start" data-testid="auth-login-helper-text">
        {helperTexts.login.text}
      </Text>
      <VStack
        w={width}
        p={4}
        mb="24px"
        borderRadius="4px"
        as="form"
        id="verifyLoginForm"
        onSubmit={handleSubmit(handleSignIn)}
      >
        <Input
          {...register('username')}
          isReadOnly
          isLabelHidden
          label={i18n.forms.labels.username}
          value={email}
        />
        <Input
          {...register('password')}
          data-testid="auth-modal-password"
          type="password"
          isLabelHidden
          label={i18n.forms.labels.password}
          error={showAlert ? '' : loginError}
          placeholder={i18n.forms.labels.password}
        />
      </VStack>
      {showAlert && loginError && (
        <Alert
          data-testid="auth-modal-login-alert"
          status="error"
          title={errorMessage}
        />
      )}
    </VStack>
  );
};
